<script lang="ts">
  import { Save } from 'lucide-svelte';
  import { LOAD_TYPES, DRIVER_TYPES } from '$lib/constants/load';
  import { setError, setLoading, uiState } from '$lib/state/ui.svelte';
  import { getErrorMessage } from '$lib/api';
  import { roomsQuery } from '$lib/queries/rooms';
  import { createLoadMutation, createLoadsMutation, updateLoadMutation } from '$lib/mutations/loads';
  import type { LoadRequest, LoadResponse } from '$lib/types/load';

  let {
    onsuccess = (_load: LoadResponse) => {},
    oncancel = () => {}
  }: {
    onsuccess?: (load: LoadResponse, count: number) => void;
    oncancel?: () => void;
  } = $props();

  const rooms = roomsQuery();
  const createLoad = createLoadMutation();
  const createLoads = createLoadsMutation();
  const updateLoad = updateLoadMutation();

  type LoadFormData = {
    isEditing?: boolean;
    load?: {
      id: number;
      room: number;
      name: string;
      load_type: string;
      quantity?: number;
      wattage_per_unit?: number;
      driver_required?: boolean;
      driver_type?: string;
      loads_per_driver?: number;
      driver_model?: string;
      supply_materials?: boolean;
      description?: string | null;
    };
  };

  let isEditingMode = $state(false);
  let editingLoadId = $state<number | null>(null);
  let selectedRoomId = $state<number | null>(null);
  let selectedName = $state<string | null>(null);
  let selectedLoadType = $state<string | null>(null);
  let selectedQuantity = $state(1);
  let createMultipleEntries = $state(false);
  let entryCount = $state<number | undefined>(undefined);
  let isSaving = $state(false);
  let isDriverRequired = $state(false);
  let selectedWattagePerUnit = $state(0);
  let selectedDriverType = $state('');
  let selectedLoadsPerDriver = $state(1);
  let selectedDriverModel = $state('');
  let supplyMaterials = $state(false);
  let selectedDescription = $state<string | null>(null);

  let dialogData = $derived(uiState.createDialogData as LoadFormData | null);
  $effect(() => {
    if (dialogData?.isEditing && dialogData.load?.id) {
      const load = dialogData.load;
      isEditingMode = true;
      createMultipleEntries = false;
      entryCount = undefined;
      editingLoadId = load.id;
      selectedRoomId = load.room;
      selectedName = load.name;
      selectedLoadType = load.load_type;
      selectedQuantity = load.quantity ?? 1;
      selectedWattagePerUnit = load.wattage_per_unit ?? 0;
      isDriverRequired = load.driver_required ?? false;
      selectedDriverType = load.driver_type ?? '';
      selectedLoadsPerDriver = load.loads_per_driver ?? 1;
      selectedDriverModel = load.driver_model ?? '';
      supplyMaterials = load.supply_materials ?? false;
      selectedDescription = load.description ?? null;
    } else if (dialogData && !dialogData.isEditing) {
      isEditingMode = false;
      editingLoadId = null;
      resetForm();
      if (dialogData.load?.room != null) {
        selectedRoomId = dialogData.load.room;
      }
    }
  });

  async function saveLoad() {
    if (isSaving) return;
    if (!selectedRoomId || !selectedName || !selectedLoadType) {
      setError('Please select a room, name, and load type.');
      return;
    }

    if (isDriverRequired && (!selectedDriverType || !selectedDriverModel)) {
      setError('Please select a driver type and model.');
      return;
    }

    const multiple = !isEditingMode && createMultipleEntries;
    if (
      multiple &&
      (entryCount == null || !Number.isInteger(entryCount) || entryCount < 1 || entryCount > 1000)
    ) {
      setError('Please enter a whole number of entries between 1 and 1000.');
      return;
    }
    const count = multiple ? entryCount! : 1;

    const loadPayload: LoadRequest = {
      room: selectedRoomId,
      name: selectedName,
      load_type: selectedLoadType,
      quantity: selectedQuantity,
      wattage_per_unit: selectedWattagePerUnit,
      driver_required: isDriverRequired,
      description: selectedDescription ?? '',
      ...(isDriverRequired && {
        driver_type: selectedDriverType,
        loads_per_driver: selectedLoadsPerDriver,
        driver_model: selectedDriverModel,
        supply_materials: supplyMaterials
      })
    };

    try {
      isSaving = true;
      setLoading(true);
      let result: LoadResponse;
      if (isEditingMode && editingLoadId != null) {
        result = await updateLoad.mutateAsync({ id: editingLoadId, request: loadPayload });
      } else if (multiple) {
        const results = await createLoads.mutateAsync({ ...loadPayload, count });
        result = results[0];
      } else {
        result = await createLoad.mutateAsync(loadPayload);
      }
      uiState.createDialogData = null;
      resetForm();
      onsuccess(result, count);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      isSaving = false;
      setLoading(false);
    }
  }

  function resetForm() {
    selectedRoomId = null;
    selectedName = null;
    selectedLoadType = null;
    selectedQuantity = 1;
    createMultipleEntries = false;
    entryCount = undefined;
    isDriverRequired = false;
    selectedWattagePerUnit = 0;
    selectedDriverType = '';
    selectedLoadsPerDriver = 1;
    selectedDriverModel = '';
    supplyMaterials = false;
    selectedDescription = null;
  }
</script>

<form class="flex flex-col space-y-4">
  <div class="grid grid-cols-2 gap-6">
    <div class="flex flex-col">
      <label for="room" class="text-sm font-medium text-text-secondary">Room</label>
      <select
        name="room"
        id="room"
        bind:value={selectedRoomId}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      >
        {#each rooms.data ?? [] as room}
          <option value={room.id}>{room.name}</option>
        {/each}
      </select>
    </div>
    <div class="flex flex-col">
      <label for="name" class="text-sm font-medium text-text-secondary">Name</label>
      <input
        type="text"
        name="name"
        id="name"
        bind:value={selectedName}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      />
    </div>
  </div>

  {#if !isEditingMode}
    <div class="grid grid-cols-2 gap-6">
      <label class="flex items-center gap-2 text-sm font-medium text-text-secondary">
        <input type="checkbox" bind:checked={createMultipleEntries} disabled={isSaving} />
        Create multiple entries
      </label>
      <div class="flex flex-col">
        <label for="entry_count" class="text-sm font-medium text-text-secondary"
          >Number of entries</label
        >
        <input
          type="number"
          name="entry_count"
          id="entry_count"
          min="1"
          max="1000"
          step="1"
          bind:value={entryCount}
          disabled={!createMultipleEntries || isSaving}
          required={createMultipleEntries}
          aria-describedby="entry_count_help"
          class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
    <p id="entry_count_help" class="text-sm text-text-secondary">
      Multiple entries use the same settings and numbered names, for example:
      {selectedName || 'lights'} 1, {selectedName || 'lights'} 2, {selectedName || 'lights'} 3.
    </p>
  {/if}

  <div class="grid grid-cols-2 gap-6">
    <div class="flex flex-col">
      <label for="load_types" class="text-sm font-medium text-text-secondary">Load Type</label>
      <select
        name="load_types"
        id="load_types"
        bind:value={selectedLoadType}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      >
        {#each Object.entries(LOAD_TYPES) as [type, config]}
          <option value={type}>{config.label}</option>
        {/each}
      </select>
    </div>
    <div class="flex flex-col">
      <label for="quantity" class="text-sm font-medium text-text-secondary">Quantity</label>
      <input
        type="number"
        name="quantity"
        id="quantity"
        bind:value={selectedQuantity}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      />
    </div>
  </div>

  <div class="grid grid-cols-2 gap-6">
    <div class="flex flex-col">
      <label for="wattage" class="text-sm font-medium text-text-secondary">Wattage Per Unit</label>
      <input
        type="number"
        name="wattage"
        id="wattage"
        bind:value={selectedWattagePerUnit}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      />
    </div>
    <div class="flex flex-col">
      <label for="driver_required" class="text-sm font-medium text-text-secondary"
        >Driver Required</label
      >
      <select
        name="driver_required"
        id="driver_required"
        bind:value={isDriverRequired}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      >
        <option value={true}>Yes</option>
        <option value={false}>No</option>
      </select>
    </div>
  </div>

  {#if isDriverRequired}
    <div class="grid grid-cols-2 gap-6">
      <div class="flex flex-col">
        <label for="driver_type" class="text-sm font-medium text-text-secondary">Driver Type</label>
        <select
          name="driver_type"
          id="driver_type"
          bind:value={selectedDriverType}
          class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
        >
          {#each Object.entries(DRIVER_TYPES) as [type, name]}
            <option value={type}>{name}</option>
          {/each}
        </select>
      </div>
      <div class="flex flex-col">
        <label for="loads_per_driver" class="text-sm font-medium text-text-secondary"
          >Loads Per Driver</label
        >
        <input
          type="number"
          name="loads_per_driver"
          id="loads_per_driver"
          bind:value={selectedLoadsPerDriver}
          class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
        />
      </div>
    </div>
    <div class="grid grid-cols-2 gap-6">
      <div class="flex flex-col">
        <label for="driver_model" class="text-sm font-medium text-text-secondary"
          >Driver Model</label
        >
        <input
          type="text"
          name="driver_model"
          id="driver_model"
          bind:value={selectedDriverModel}
          class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
        />
      </div>
      <div class="flex flex-col">
        <label for="supply_materials" class="text-sm font-medium text-text-secondary"
          >Supply Materials</label
        >
        <select
          name="supply_materials"
          id="supply_materials"
          bind:value={supplyMaterials}
          class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
        >
          <option value={true}>Yes</option>
          <option value={false}>No</option>
        </select>
      </div>
    </div>
  {/if}
  <div class="flex flex-col items-start">
    <label for="description" class="text-sm font-medium text-text-secondary">Description</label>
    <textarea
      name="description"
      id="description"
      bind:value={selectedDescription}
      class="mt-1 w-full px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
    ></textarea>
  </div>

  <div class="flex justify-end">
    <button
      type="button"
      onclick={saveLoad}
      disabled={isSaving}
      class="mt-4 inline-flex items-center gap-2 px-6 py-2 bg-accent-primary text-white rounded-lg shadow-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent-primary"
    >
      <Save class="w-5 h-5" />
      {isSaving ? 'Saving…' : isEditingMode ? 'Save Load' : createMultipleEntries ? 'Create Loads' : 'Create Load'}
    </button>
  </div>
</form>
